import { RegionShared } from "@1games-ru/region-shared";
import { useMemo } from "react";
import clsx from "clsx";
import { MAX_OIL_TEMPERATURE } from "@/core/constants/vehicleLimits";
import { Icon } from "@/shared/ui";
import IndicatorCounter from "@/domains/hud/components/speedometer/indicator/IndicatorCounter";

type Props = {
  className?: string;
} & typeof RegionShared.Cef.Requests.SetVehicleStateRequest;

export default function HudSpeedometerMaximal({
  className,
  fuel,
  fuelCapacity,
  isLight,
  isEngine,
  isDoors,
  isSeatBelt,
  isHandBreak,
  isEmergency,
  isLeftTurn,
  isRightTurn,
  oilTemperature,
  speed,
  gear,
  mileage,
}: Props) {
  const fuelPercent = fuelCapacity === 0 ? 0 : Number(((fuel / fuelCapacity) * 100).toFixed(0));
  const oilTemperaturePercent =
    oilTemperature === 0 ? 0 : Number(((MAX_OIL_TEMPERATURE / oilTemperature) * 100).toFixed(0));

  const newGear = useMemo(() => {
    if (Number(gear) === 0) {
      return "N";
    }
    if (Number(gear) < 0) {
      return "R";
    }

    return String(gear);
  }, [gear]);

  return (
    <div className={clsx("relative", className)}>
      <Icon
        name="maximal-speedometer-bg"
        category="speedometer"
        className="absolute -top-[30px] -left-[60px] w-[294px] h-[218px] z-0"
      />
      <Icon
        name="maximal-speedometer-temperature"
        category="speedometer"
        className="absolute h-[77px] w-[49px] right-0 -bottom-[10px] z-0"
      />
      <div className="absolute right-0 -bottom-[10px] h-[77px] w-[49px]">
        <div className="relative w-full h-full">
          <Icon
            name="maximal-speedometer-temperature"
            category="speedometer"
            className="w-full h-full absolute top-0 right-0 z-0 bg-[#3183ff]/50"
          />
          <Icon
            name="maximal-speedometer-temperature"
            category="speedometer"
            className="w-full h-full absolute top-0 right-0 z-0 bg-[#3183FF]"
            style={{
              clipPath: `polygon(0% ${100 - oilTemperaturePercent}%, 100% ${100 - oilTemperaturePercent}%, 100% 100% , 0% 100%)`,
            }}
          />
        </div>
      </div>
      <div className="absolute right-[49px] -bottom-[10px] h-[8px] w-[90px]">
        <div className="relative w-full h-full">
          <Icon
            name="maximal-speedometer-fuel"
            category="speedometer"
            className="w-full h-full absolute top-0 right-0 z-0 bg-[#FFB45C]/40"
          />
          <Icon
            name="maximal-speedometer-fuel"
            category="speedometer"
            className="w-full h-full absolute top-0 right-0 z-0 bg-[#FFB45C]"
            style={{
              clipPath: `polygon(0px 0px, ${fuelPercent}% 0%, ${fuelPercent}% 100%, 0% 100%)`,
            }}
          />
        </div>
      </div>
      <div className="flex flex-col items-start z-1">
        <div className="flex items-start gap-5">
          <Icon
            name="light"
            category="speedometer"
            className={clsx("w-6", isLight ? "bg-white" : "bg-white/60")}
          />
          <Icon
            name="seatbelt"
            category="speedometer"
            className={clsx("w-6", isSeatBelt ? "bg-[#FF3434]" : "bg-white/60")}
          />
          <Icon
            name="doors"
            category="speedometer"
            className={clsx("w-6", isDoors ? "bg-white" : "bg-white/60")}
          />
          <Icon
            name="engine"
            category="speedometer"
            className={clsx("w-6", isEngine ? "bg-[#84FF97]" : "bg-white/60")}
          />
          <Icon
            name="hand-break"
            category="speedometer"
            className={clsx("w-6", isHandBreak ? "bg-[#FF3434]" : "bg-white/60")}
          />
        </div>
        <div className="flex gap-8 mt-4.5">
          <div>
            <IndicatorCounter
              value={speed}
              cols={3}
              ceilHeight={40}
              direction="right"
              disableNotUsedCeil
              className="h-[42px] gap-0.5"
              classNameCeil="w-[23px]"
              classNameStep="text-[2.25rem] font-black text-white tabular-nums"
            />
            <span className="text-12 font-extrabold text-white">KM/H</span>
          </div>
          <div>
            <IndicatorCounter
              value={mileage}
              cols={5}
              ceilHeight={25}
              disableNotUsedCeil
              className="h-[42px] gap-1 items-center"
              classNameCeil="w-[25px] h-[25px] rounded-[6px] border border-white/40 bg-clip-content bg-gradient-to-t from-white/15 to-white/0"
              classNameStep="text-15 font-extrabold text-white"
            />
            <div className="text-12 font-extrabold text-white/65 text-center inline-block w-full">
              ОБЩИЙ ПРОБЕГ
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-[25px] h-[25px] rounded-[6px] bg-white text-16 font-extrabold text-black mix-blend-screen">
              {newGear}
            </div>
            <div className="flex items-center gap-4 ml-[47px]">
              <Icon
                name="arrow-left"
                category="speedometer"
                className={clsx("w-5", isLeftTurn ? "bg-[#84FF97]" : "bg-white/60")}
              />
              <Icon
                name="emergency"
                category="speedometer"
                className={clsx("w-5", isEmergency ? "bg-[#FF3434]" : "bg-white/60")}
              />
              <Icon
                name="arrow-right"
                category="speedometer"
                className={clsx("w-5", isRightTurn ? "bg-[#84FF97]" : "bg-white/60")}
              />
            </div>
            <Icon
              name="temperature"
              category="speedometer"
              className="w-7 bg-[#3183FF] ml-[45px]"
            />
          </div>
          <div className="flex items-center justify-end mt-4">
            <Icon name="fuel" category="speedometer" className="w-6 bg-[#FFB45C]" />
            <div className="flex items-center gap-1.5 ml-2">
              <div className="w-4 text-12 font-extrabold text-white text-right tabular-nums">
                {fuel}
              </div>
              <div className="w-[2px] h-[10px] bg-white/60" />
              <div className="w-4 text-12 font-extrabold text-white/60 tabular-nums">
                {fuelCapacity}
              </div>
            </div>
            <div className="w-7 flex items-center justify-center text-12 text-right font-extrabold text-white ml-[18px] tabular-nums">
              {oilTemperature}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
